import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import type * as Y from "yjs";
import type { Awareness } from "y-protocols/awareness";

/**
 * Binding Yjs to React.
 *
 * `useSyncExternalStore` needs a snapshot that is referentially stable between
 * changes, or React re-renders forever. Yjs reads build fresh arrays every
 * time, so the value is cached and only recomputed when an observer says the
 * data actually moved.
 *
 * The cache is created with `useMemo` keyed on the source, rather than a ref
 * compared during render. A fresh source gets a fresh cache for free, and
 * nothing touches a ref while rendering — which matters under concurrent
 * rendering, where a render can be thrown away and restarted.
 *
 * SubCoach instead caches Yjs data inside Zustand and re-reads it after its
 * own writes. That works for a single user on two devices and fails completely
 * here: a teammate's change lands in the document and in IndexedDB, but
 * nothing tells React to look. For a live command tool that is the whole
 * feature, so the subscription is done properly.
 */

interface Cache<T> {
  value: T;
  dirty: boolean;
}

export function useYjsValue<T>(
  doc: Y.Doc | null,
  mapName: string,
  read: (doc: Y.Doc) => T,
  fallback: T,
): T {
  const readRef = useRef(read);
  readRef.current = read;

  // A new document means everything cached about the old one is meaningless.
  const cache = useMemo<Cache<T>>(
    () => ({ value: fallback, dirty: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, mapName],
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!doc) return () => undefined;
      const map = doc.getMap(mapName);
      const handler = (): void => {
        cache.dirty = true;
        onChange();
      };
      map.observeDeep(handler);
      return () => map.unobserveDeep(handler);
    },
    [doc, mapName, cache],
  );

  const getSnapshot = useCallback((): T => {
    if (!doc) return fallback;
    if (cache.dirty) {
      cache.value = readRef.current(doc);
      cache.dirty = false;
    }
    return cache.value;
  }, [doc, fallback, cache]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Live presence, straight from awareness.
 *
 * Awareness is the difference between "in the roster" and "here right now".
 * Command depends on it: only connected players can vote, and a commander is
 * only replaced after they have actually gone.
 */
export function useAwarenessStates<T>(
  awareness: Awareness | null,
  parse: (state: unknown) => T | undefined,
): T[] {
  const parseRef = useRef(parse);
  parseRef.current = parse;

  const empty = useMemo<T[]>(() => [], []);
  const cache = useMemo<Cache<T[]>>(
    () => ({ value: [], dirty: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [awareness],
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!awareness) return () => undefined;
      const handler = (): void => {
        cache.dirty = true;
        onChange();
      };
      awareness.on("change", handler);
      return () => awareness.off("change", handler);
    },
    [awareness, cache],
  );

  const getSnapshot = useCallback((): T[] => {
    if (!awareness) return empty;
    if (cache.dirty) {
      const next: T[] = [];
      for (const state of awareness.getStates().values()) {
        const parsed = parseRef.current(state);
        if (parsed !== undefined) next.push(parsed);
      }
      cache.value = next;
      cache.dirty = false;
    }
    return cache.value;
  }, [awareness, cache, empty]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
